export type TournamentStatus = 'Draft' | 'Active' | 'Completed' | 'Archived';
export type ImageProvider = 'OpenAi' | 'NanoBanana';
export type PosterRenderMode = 'AiArtWithTextOverlay' | 'FullyAiGenerated';
export type PosterFormat = 'Portrait45' | 'Portrait23' | 'Square' | 'Portrait' | 'Landscape';
export type PosterTemplate = 'Top5' | 'Fixture' | 'Announcement';
export type PosterStatus = 'Draft' | 'Pending' | 'Generating' | 'Ready' | 'Failed';
export type PlayerPosition = 'Unspecified' | 'Lead' | 'Second' | 'Third' | 'Skip';

export interface Tournament {
  id: string;
  name: string;
  season: string;
  description?: string | null;
  venue?: string | null;
  startDate: string;
  endDate: string;
  weeks: number;
  template: PosterTemplate;
  primaryColour: string;
  accentColour: string;
  status: TournamentStatus;
  backgroundBlobName?: string | null;
  backgroundUrl?: string | null;
  createdUtc: string;
  updatedUtc: string;
}

export interface TournamentRequest {
  name: string;
  season: string;
  startDate: string;
  endDate: string;
  weeks: number;
  description?: string | null;
  venue?: string | null;
  template?: PosterTemplate | null;
  primaryColour?: string | null;
  accentColour?: string | null;
  status?: TournamentStatus | null;
}

export interface Player {
  name: string;
  position: PlayerPosition;
}

export interface Match {
  id: string;
  tournamentId: string;
  week: number;
  roundName?: string | null;
  startsAt: string;
  venue?: string | null;
  opponent: string;
  isHome: boolean;
  players: Player[];
  notes?: string | null;
}

export interface MatchRequest {
  week: number;
  roundName?: string | null;
  startsAt: string;
  venue?: string | null;
  opponent: string;
  isHome: boolean;
  players: Player[];
  notes?: string | null;
}

/** One image generated for a poster. Regenerating adds another rather than replacing this one. */
export interface PosterVersion {
  id: string;
  url?: string | null;
  provider: ImageProvider;
  modelUsed?: string | null;
  createdUtc: string;
  isCurrent: boolean;
}

export interface Poster {
  id: string;
  tournamentId: string;
  matchId?: string | null;
  provider: ImageProvider;
  renderMode: PosterRenderMode;
  format: PosterFormat;
  template?: PosterTemplate | null;
  status: PosterStatus;
  stylePreset: string;
  headline?: string | null;
  subheadline?: string | null;
  callToAction?: string | null;
  resolvedPrompt?: string | null;
  requestedModel?: string | null;
  modelUsed?: string | null;
  caption?: string | null;
  expectedText: string[];
  posterUrl?: string | null;
  artworkUrl?: string | null;
  /** Newest first. */
  versions: PosterVersion[];
  errorMessage?: string | null;
  createdUtc: string;
  completedUtc?: string | null;
}

export interface CreatePosterRequest {
  matchId?: string | null;
  provider: ImageProvider;
  renderMode: PosterRenderMode;
  format: PosterFormat;
  template?: PosterTemplate | null;
  model?: string | null;
  stylePreset?: string | null;
  headline?: string | null;
  subheadline?: string | null;
  callToAction?: string | null;
  promptOverride?: string | null;
}

export interface ProviderInfo {
  provider: ImageProvider;
  displayName: string;
  /** The model used when a poster does not name one. */
  modelId: string;
  available: boolean;
  /** Every model a poster may choose for this provider, default first. */
  models: string[];
}

export const POSTER_FORMATS: Array<{ value: PosterFormat; label: string; hint: string }> = [
  { value: 'Portrait45', label: 'Portrait 1080 x 1350', hint: 'Facebook and Instagram feed - best for posters' },
  { value: 'Portrait23', label: 'Poster 1080 x 1620', hint: 'Traditional poster shape, good for printing' },
  { value: 'Square', label: 'Square 1080 x 1080', hint: 'Square feed post' },
  { value: 'Portrait', label: 'Story 1080 x 1920', hint: 'Stories and WhatsApp status' },
  { value: 'Landscape', label: 'Landscape 1200 x 630', hint: 'Facebook link posts' },
];

export const POSTER_TEMPLATES: Array<{ value: PosterTemplate; label: string; hint: string }> = [
  { value: 'Top5', label: 'Top 5 good luck', hint: 'Greeting, the fixture, and a numbered table of the selected side.' },
  { value: 'Fixture', label: 'Match announcement', hint: 'The fixture leads, with players listed underneath if given.' },
  { value: 'Announcement', label: 'Competition notice', hint: 'No single fixture - for entries opening, finals day and the like.' },
];

export const RENDER_MODES: Array<{ value: PosterRenderMode; label: string; hint: string }> = [
  {
    value: 'AiArtWithTextOverlay',
    label: 'AI artwork + crisp text',
    hint: 'The model paints the background, the server draws the fixture details as sharp vector text. Names and dates are always correct.',
  },
  {
    value: 'FullyAiGenerated',
    label: 'Fully AI generated',
    hint: 'The model draws the whole poster, ribbons and lettering included. Closest to the club house style, but always proofread the names against the checklist before posting.',
  },
];

export const STYLE_PRESETS = [
  'Club heritage',
  'Photographic',
  'Bold graphic',
  'Retro club poster',
  'Minimal editorial',
  'Sunset dramatic',
];

// ------------------------------------------------------------------ templates

export type PosterFieldType = 'Text' | 'MultilineText' | 'Date' | 'Time' | 'List' | 'Choice';

export interface PosterField {
  key: string;
  label: string;
  type: PosterFieldType;
  required: boolean;
  placeholder?: string | null;
  helpText?: string | null;
  defaultValue?: string | null;
  maxLength?: number | null;
  minItems?: number | null;
  maxItems?: number | null;
  options?: string[] | null;
}

/** A template and the data model of the poster it produces. The form is built from `fields`. */
export interface PosterTemplateDefinition {
  template: PosterTemplate;
  name: string;
  description: string;
  defaultFormat: PosterFormat;
  fields: PosterField[];
}

export interface CreatePosterFromTemplateRequest {
  template: PosterTemplate;
  /** Field key to value. List fields are newline separated. */
  values: Record<string, string>;
  provider?: ImageProvider | null;
  renderMode?: PosterRenderMode | null;
  format?: PosterFormat | null;
  model?: string | null;
  stylePreset?: string | null;
  /** False saves a draft: stored and validated, but no image model is called. */
  generate?: boolean;
}

export interface CreatedPoster {
  id: string;
  tournamentId: string;
  tournamentName: string;
  matchId?: string | null;
  template: PosterTemplate;
  format: PosterFormat;
  status: PosterStatus;
}
